import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { Button, Dropdown, message, Modal, Select } from 'antd'
import type { MenuProps } from 'antd'
import { useBackgroundNavigate, useSearchParams } from '@shared/hooks'
import * as Icons from '@ant-design/icons'
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns'
import { StopPropagation } from '@shared/components/stop-propagation'
import { localURLMaker } from '@shared/utils/url-maker'
import { filterOption } from '@shared/modules/form/utils'
import { Warehouse } from '../types'
import { WarehousesTableContext } from '../context'
import { warehouseQueryKeys } from '../utils/warehouse-query-keys'
import { WarehousesService } from '../services'
import { useStatusList } from '../../status/hooks/use-list'
import { useDepartmentList } from '../../departments/hooks'
import { NextTableCheckboxFilter } from '@shared/modules/next-table/components/filters/checkbox'
import { NextTableCheckCell } from '@shared/modules/next-table/components/cells/check'

export const useWarehousesTable = () => {
  const { handleFetch } = useContext(WarehousesTableContext)
  const navigate = useBackgroundNavigate()
  const searchParams = useSearchParams<{ reFetchWarehousesTable?: string }>()

  const statusCollection = useStatusList({ modelId: 23 })
  const departmentCollection = useDepartmentList()

  const getRowProps = useCallback(
    (id) => ({
      onDoubleClick: () => navigate(localURLMaker('/warehouses/:id/details', { id }), { withBackground: true }),
    }),
    [navigate],
  )

  const columns = useMemo<Column<Warehouse>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }) => {
          const goTo = useCallback(
            (type: string, withBackground = false) => {
              navigate(localURLMaker('/warehouses/:id/:type', { id: original.id, type }), { withBackground })
            },
            [original.id],
          )

          const handleDelete = useCallback(() => {
            Modal.confirm({
              content: 'Anbarı silmək istəyirsiniz?',
              okText: 'Sil',
              onOk: async () => {
                const response = await WarehousesService.deleteById(original.id)
                if (response.status === 200) {
                  message.success(response.data)
                  navigate(localURLMaker('/warehouses', {}, { reFetchWarehousesTable: true }))
                } else {
                  message.error(response.data)
                }
              },
            })
          }, [original])

          const handleUpdateState = useCallback(
            async (stateId: number | string) => {
              const handler = async () => {
                message.loading({ key: 'message', content: 'Əməliyyat aparılır.' })
                const result = await WarehousesService.updateStateById({ id: original.id, state_id: stateId })

                if (result.status === 200) {
                  message.success({ key: 'message', content: 'Əməliyyat müvəffəqiyyətlə başa çatdı' })
                  navigate(localURLMaker('/warehouses', {}, { reFetchWarehousesTable: true }))
                } else {
                  message.error({ key: 'message', content: result.data as string })
                }
              }

              Modal.confirm({
                title: 'Status dəyişməyə əminsinizmi?',
                content: 'Status dəyişdikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
                onOk: handler,
              })
            },
            [original.id],
          )

          const menuItems: MenuProps['items'] = [
            {
              key: 'update',
              label: 'Dəyişdir',
              icon: <Icons.EditOutlined />,
              onClick: () => goTo('update', true),
            },
            {
              key: 'details',
              label: 'Ətraflı',
              icon: <Icons.FileOutlined />,
              onClick: () => goTo('details', false),
            },
            {
              key: 'status-change',
              label: 'Status dəyiş',
              icon: <Icons.AppstoreOutlined />,
              children: statusCollection.data?.map((status) => ({
                key: `status-${status.id}`,
                label: status.name,
                onClick: () => handleUpdateState(status.id),
              })),
            },
            {
              key: 'delete',
              label: 'Sil',
              icon: <Icons.DeleteOutlined />,
              onClick: handleDelete,
            },
          ]

          return (
            <StopPropagation>
              <Dropdown menu={{ items: menuItems }}>
                <Button size='small' icon={<Icons.MoreOutlined />} />
              </Dropdown>
            </StopPropagation>
          )
        },
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: warehouseQueryKeys.id,
        Header: 'Kod',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.name,
        id: warehouseQueryKeys.name,
        Header: 'Ad',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.code,
        id: warehouseQueryKeys.code,
        Header: 'Prefix',
      },
      {
        accessor: (row) => row.department.name,
        id: warehouseQueryKeys.departmentId,
        Header: 'Şöbə',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select showSearch filterOption={filterOption} style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {departmentCollection.data?.map((department) => (
              <Select.Option key={department.id} value={department.id.toString()}>
                {department.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        accessor: (row) => row.state.name,
        id: warehouseQueryKeys.stateId,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select showSearch filterOption={filterOption} style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {statusCollection.data?.map((status) => (
              <Select.Option key={status.id} value={status.id.toString()}>
                {status.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.isMain,
        id: warehouseQueryKeys.isMain,
        Header: 'Əsas anbar',
        Filter: NextTableCheckboxFilter,
        Cell: NextTableCheckCell,
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.createdAt,
        id: warehouseQueryKeys.createdAt,
        Header: 'Yaradılma tarixi',
        Filter: () => null,
      },
    ],
    [navigate, statusCollection.data, departmentCollection.data],
  )

  useEffect(() => {
    ; (async () => {
      if (searchParams.searchParams.reFetchWarehousesTable) {
        searchParams.remove.current('reFetchWarehousesTable')
        await handleFetch()
      }
    })()
  }, [searchParams.searchParams.reFetchWarehousesTable, searchParams.remove, handleFetch])

  return { columns, getRowProps }
}
